// MACHINERY V2 (Stroje) — PREHĽAD, the profile's first tab (M2.16d, board 20).
//
// Spec: mobile/README.md §"Profil (20–24)" — "Prehľad (20) is NEW: live status
// card (program mono, 3 KPI cells RPM/F/S1), events list with pips, 'Spýtať sa
// Midas' ghost CTA (brand accent, not module accent — Midas is global)" — plus
// MANAGER RULING Q1-M2.16, recorded in profileTabContract.ts.
//
// IT REPLACES THE `live` PLACEHOLDER AT ALL BREAKPOINTS and is UNGATED for
// unconnected machines. The full amendment (and why the contract member was
// renamed `live` -> `overview`) lives in profileTabContract.ts; it is not
// restated here, because a rule with two homes is a rule with two versions.
//
// ============================================================
//  TWO SOURCES, ONE LIST — AND NEITHER OF THEM IS FETCHED HERE
// ============================================================
// The card merges gateway EVENTS with SERVICE REPORTS.
//
//  · EVENTS come from usePrehladEvents (this folder), which owns the one
//    gateway request for this machine and is GATED on the machine actually
//    having a gateway connection — see that file's `enabled` block.
//  · SERVICE REPORTS come from ProfileBody's single `useSrTabQuery` instance,
//    handed down as a prop. This tab does NOT mount its own (M2.16d review,
//    finding F-A): two instances of that hook meant two per-instance owners of
//    ONE app-wide Redux slot, so an instance unmounting mid-flight could write a
//    slot the surviving instance was no longer allowed to read. ProfileBody's
//    header carries the full reasoning; the consequence here is that Prehľad and
//    Servisné správy read exactly one query, one identity guard and one default
//    sort (OpenDate desc — newest first, which is what a recent-activity list
//    wants), and that this tab now gets the SR `failed` / `refetch` pair it used
//    to discard.
//
// TELEMETRY IS PASSED IN, NEVER RE-FETCHED. MachineProfileV2 already runs the
// ONE fleet poller for this machine and derives the presentation once; this tab
// receives both. A poller here would be a second truth about the same gateway.
//
// >>> NOTHING FACTUAL IS SAID BEFORE BOTH SOURCES HAVE SETTLED (review F2).
// <<< "No activity recorded for this machine yet" is a CLAIM, and it used to
// paint on the first frame of every profile open — on the tab that is now the
// default for nearly every machine — because the list was rendered with no
// loading gate at all. It is gated the same way every sibling tab gates its
// own (`showSkeleton = !ready && !failed`, ServiceReportsTabV2.tsx /
// PartsTabV2.tsx), against BOTH halves, because a half-settled list is a claim
// about a machine we have only half looked at.
//
// AN EMPTY ARRAY IS STILL NOT AN ERROR. The gate above delays the empty state;
// it never converts it into one. See usePrehladEvents.ts.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import {
  MachineryV2CardPresentation,
  MachineryV2Telemetry,
} from "../../fleet/cardStates";
import RetryNotice from "../../feedback/RetryNotice";
import { srDetailPath } from "../../sr/srNavigation";
import { TabCard } from "../tabs/tabAtoms";
import { SkeletonList } from "../tabs/tabSkeletons";
import { SrTabQuery } from "../tabs/useSrTabQuery";
import PrehladEventList from "./PrehladEventList";
import PrehladLiveCard from "./PrehladLiveCard";
import {
  EVENT_LABEL_KEYS,
  UNKNOWN_EVENT_LABEL_KEY,
  eventRow,
  mergePrehladRows,
  srRow,
} from "./prehladEvents";
import usePrehladEvents from "./usePrehladEvents";

export interface PrehladTabProps {
  machineId: string;
  telemetry: MachineryV2Telemetry;
  presentation: MachineryV2CardPresentation;
  /** ProfileBody's single service-report query — see the header. */
  srQuery: SrTabQuery;
}

const PrehladTab: React.FC<PrehladTabProps> = ({
  machineId,
  telemetry,
  presentation,
  srQuery,
}) => {
  const { translate } = useTranslation("myMachinery");
  const navigate = useNavigate();

  const {
    events,
    failed: eventsFailed,
    loading: eventsLoading,
    settled: eventsSettled,
    refetch,
  } = usePrehladEvents(machineId, presentation.showStatusRow);
  // Only these four members are read. The search / sort / status machinery the
  // same object also carries belongs to the Servisné správy tab and is simply
  // not driven from here.
  const {
    reports,
    failed: srFailed,
    loading: srLoading,
    refetch: srRefetch,
  } = srQuery;

  const rows = useMemo(() => {
    const eventRows = events.map((e) =>
      eventRow(
        e,
        translate(EVENT_LABEL_KEYS[e.eventType] || UNKNOWN_EVENT_LABEL_KEY)
      )
    );
    const srRows = (reports || []).map((r) =>
      srRow(r, () => navigate(srDetailPath(machineId, r.number)))
    );
    return mergePrehladRows(eventRows, srRows);
  }, [events, reports, translate, navigate, machineId]);

  /** The SR half has spoken: the shared slot provably holds THIS machine's list
   *  (`reports !== undefined` IS the identity guard), or its fetch rejected. */
  const srSettled = reports !== undefined || srFailed;
  const showSkeleton = !(eventsSettled && srSettled);
  /** "No activity recorded" is a CLAIM about the machine, so it may not be
   *  spoken over a failed fetch — the sibling tabs' `&& !failed` rule. With
   *  rows in hand the list still renders: half a list beats none, and the retry
   *  notice above says what is missing. */
  const showList = !showSkeleton && !(rows.length === 0 && (eventsFailed || srFailed));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 9, minWidth: 0 }}>
      <PrehladLiveCard telemetry={telemetry} presentation={presentation} />

      {/* ONLY a REJECTED events request shows this. An empty array is the
          normal empty state for every machine without a gateway — see
          usePrehladEvents.ts. Rendering a failure on `[]` would put a
          broken-looking panel on ~99% of the fleet. `busy` genuinely renders
          now: `failed` survives a retry until that retry settles (item 4). */}
      {eventsFailed && (
        <RetryNotice
          title={translate("myMachinery:v2.profile.overview.eventsFailed")}
          hint={translate("myMachinery:v2.tabs.loadFailedHint")}
          onRetry={refetch}
          busy={eventsLoading}
        />
      )}

      {/* THE SR HALF GETS THE SAME TREATMENT, WHICH IT DID NOT HAVE (review
          F3). On the ~99% of machines with no gateway events, a 500 or a
          dropped connection used to render as the honest-looking "no activity"
          empty state, on the DEFAULT tab, with no way to retry. The copy is the
          Servisné správy tab's own failure string, because it is the same fetch
          failing — a second wording would be a second fact. */}
      {srFailed && (
        <RetryNotice
          title={translate("myMachinery:v2.tabs.sr.loadFailed")}
          hint={translate("myMachinery:v2.tabs.loadFailedHint")}
          onRetry={srRefetch}
          busy={srLoading}
        />
      )}

      {showSkeleton && (
        <TabCard>
          <SkeletonList rows={4} height={40} />
        </TabCard>
      )}

      {showList && (
        <PrehladEventList
          rows={rows}
          emptyText={translate("myMachinery:v2.profile.overview.noActivity")}
        />
      )}
    </div>
  );
};

export default PrehladTab;
